import { z } from "zod";
import { ClassificationSchema } from "./publication";
import { FAQItemSchema, FileItemSchema } from "./knowledge-base";

// ============================================================================
// STORE LISTING SCHEMAS
// ============================================================================

export const StoreListingStatusSchema = z.enum([
  "not_published",
  "pending_review",
  "approved",
  "rejected",
  "suspended",
]);

export const ResourceTypeSchema = z.enum(["agent", "knowledge_base"]);

export const StoreListingSchema = z.object({
  id: z.string().uuid(),
  resourceType: ResourceTypeSchema,
  resourceId: z.string().uuid(),

  sellerId: z.string().uuid(),
  sellerWorkspaceId: z.string().uuid(),

  price: z.number().min(0),
  currency: z.string().default("EUR"),

  status: StoreListingStatusSchema,
  isActive: z.boolean(),

  createdAt: z.string(),
  publishedAt: z.string().nullable(),
  suspendedAt: z.string().nullable(),

  publicationRequestedAt: z.string().nullable(),
  publicationReviewedAt: z.string().nullable(),
  publicationApprovedAt: z.string().nullable(),
  publicationRejectedAt: z.string().nullable(),
  publicationReviewerId: z.string().uuid().nullable(),
  publicationRejectionReason: z.string().nullable(),
  publicationSuspensionReason: z.string().nullable(),

  featured: z.boolean(),
  featuredOrder: z.number().nullable(),
  imageUrl: z.string().nullable(),

  salesCount: z.number(),
  totalRevenue: z.number(),
  rating: z.number().nullable(),
  reviewCount: z.number(),

  currentReleaseVersion: z.number(),
  lastUpdatedAt: z.string().nullable(),
  detailedDescription: z.string().nullable(),
});

export const StoreListingWithDetailsSchema = StoreListingSchema.extend({
  // Resource data (dénormalisé)
  name: z.string(),
  description: z.string().nullable(),
  detailedDescription: z.string().nullable(),
  sellerName: z.string(),

  // Champs supplémentaires de la vue
  averageRating: z.number().nullable(),
  viewCount: z.number(),
  featuredUntil: z.string().nullable(),
  updatedAt: z.string(),
  imageUrl: z.string().nullable(),
  initialAuthorName: z.string().nullable(),
}).merge(ClassificationSchema);

export const CreateStoreListingSchema = z.object({
  resourceType: ResourceTypeSchema,
  resourceId: z.string().uuid(),
  price: z.number().min(0),
  imageUrl: z.string().url().optional(),
  detailedDescription: z.string().optional(),
});

export const UpdateStoreListingSchema = z.object({
  price: z.number().min(0).optional(),
  isActive: z.boolean().optional(),
  detailedDescription: z.string().optional(),
});

// ============================================================================
// STORE LICENSE SCHEMAS
// ============================================================================

export const LicenseTypeSchema = z.enum(["permanent", "trial"]);
export const LicenseStatusSchema = z.enum(["active", "suspended", "revoked"]);

export const StoreLicenseSchema = z.object({
  id: z.string().uuid(),
  listingId: z.string().uuid(),

  licenseeId: z.string().uuid(),
  workspaceId: z.string().uuid(),

  licenseType: LicenseTypeSchema,
  status: LicenseStatusSchema,

  createdAt: z.string(),
  activatedAt: z.string().nullable(),
  suspendedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),

  pricePaid: z.number(),
  currency: z.string(),

  paymentMethod: z.string().nullable(),
  stripePaymentIntentId: z.string().nullable(),
  stripeCustomerId: z.string().nullable(),

  resourceType: ResourceTypeSchema,
  importedResourceId: z.string().uuid().nullable(),

  lastUsedAt: z.string().nullable(),
  usageCount: z.number(),

  metadata: z.record(z.string(), z.unknown()).nullable(),
});

export const StoreLicenseWithDetailsSchema = StoreLicenseSchema.extend({
  workspaceName: z.string(),
  resourceName: z.string(),
  listingPrice: z.number(),
});

export const PurchaseLicenseRequestSchema = z.object({
  listingId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  skipPayment: z.boolean().optional(),
});

export const ImportVersionRequestSchema = z.object({
  sourceVersion: z.number().int().positive(),
});

// ============================================================================
// STORE REVIEW SCHEMAS
// ============================================================================

export const StoreReviewSchema = z.object({
  id: z.string().uuid(),
  listingId: z.string().uuid(),
  licenseId: z.string().uuid(),
  reviewerId: z.string().uuid(),

  rating: z.number().int().min(1).max(5),
  title: z.string().nullable(),
  comment: z.string().nullable(),

  createdAt: z.string(),
  updatedAt: z.string(),
});

export const StoreReviewWithDetailsSchema = StoreReviewSchema.extend({
  reviewerName: z.string(),
});

export const CreateStoreReviewSchema = z.object({
  licenseId: z.string().uuid(),
  rating: z.number().int().min(1).max(5),
  title: z.string().optional(),
  comment: z.string().optional(),
});

export const UpdateStoreReviewSchema = z.object({
  rating: z.number().int().min(1).max(5).optional(),
  title: z.string().optional(),
  comment: z.string().optional(),
});

// ============================================================================
// STORE LISTING VERSION SCHEMAS
// ============================================================================

export const StoreListingVersionSchema = z.object({
  id: z.string().uuid(),
  listingId: z.string().uuid(),

  releaseVersion: z.number().int(),
  resourceId: z.string().uuid(),

  releaseNote: z.string().nullable(),
  changelog: z.string().nullable(),

  publishedAt: z.string(),
  deprecatedAt: z.string().nullable(),

  salesCount: z.number(),
});

export const PublishUpdateRequestSchema = z.object({
  newReleaseVersion: z.number().int().positive(),
  changelog: z.string(),
  releaseNote: z.string().optional(),
});

// ============================================================================
// STORE LICENSE HISTORY SCHEMAS
// ============================================================================

export const LicenseEventTypeSchema = z.enum(["created", "activated", "suspended", "revoked", "reactivated"]);

export const StoreLicenseHistorySchema = z.object({
  id: z.string().uuid(),
  licenseId: z.string().uuid(),

  eventType: LicenseEventTypeSchema,
  eventDate: z.string(),
  triggeredBy: z.string().uuid().nullable(),

  previousStatus: z.string().nullable(),
  newStatus: z.string().nullable(),
  reason: z.string().nullable(),

  amountPaid: z.number().nullable(),
  paymentIntentId: z.string().nullable(),

  metadata: z.record(z.string(), z.unknown()).nullable(),
});

// ============================================================================
// RESPONSE SCHEMAS
// ============================================================================

export const GetListingsResponseSchema = z.object({
  listings: z.array(StoreListingWithDetailsSchema),
  totalCount: z.number(),
  page: z.number(),
  limit: z.number(),
});

// Resource preview schemas for store listing detail page
export const AgentPreviewSchema = z.object({
  type: z.literal("agent"),
  data: z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    workflow: z.any(), // JSONB - contains workflow with phases info
    specialty: z.string().nullable(),
    usageContext: z.string().nullable(), // camelCase pour cohérence frontend
    operations: z.any(),
    patientTypes: z.any(), // camelCase pour cohérence frontend
    tags: z.array(z.string()),
    status: z.string(),
    releaseVersion: z.number().nullable(), // camelCase pour cohérence frontend
  }),
});

export const KnowledgeBasePreviewSchema = z.object({
  type: z.literal("knowledge_base"),
  data: z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    specialty: z.string().nullable(),
    usageContext: z.string().nullable(), // camelCase pour cohérence frontend
    operations: z.any(),
    patientTypes: z.any(), // camelCase pour cohérence frontend
    tags: z.array(z.string()),
    status: z.string(),
    releaseVersion: z.number().nullable(), // camelCase pour cohérence frontend
    preview: z.object({
      faq: z.array(FAQItemSchema.pick({ question: true, answer: true })),
      documents: z.array(FileItemSchema.pick({ name: true, type: true, uploadDate: true })),
      stats: z.object({
        documentCount: z.number(),
      }),
    }),
  }),
});

export const ResourcePreviewSchema = z.union([AgentPreviewSchema, KnowledgeBasePreviewSchema]).nullable();

export const GetListingResponseSchema = z.object({
  listing: StoreListingWithDetailsSchema,
  reviews: z.array(StoreReviewWithDetailsSchema),
  resource: ResourcePreviewSchema,
});

export type ResourcePreview = z.infer<typeof ResourcePreviewSchema>;

export const GetReviewsResponseSchema = z.object({
  reviews: z.array(StoreReviewWithDetailsSchema),
  total: z.number(),
  averageRating: z.number().nullable(),
  ratingDistribution: z.object({
    1: z.number(),
    2: z.number(),
    3: z.number(),
    4: z.number(),
    5: z.number(),
  }),
});

export const GetMyLicensesResponseSchema = z.object({
  licenses: z.array(StoreLicenseWithDetailsSchema),
});

export const GetAvailableUpdatesResponseSchema = z.object({
  updates: z.array(
    z.object({
      importedResourceId: z.string().uuid(),
      resourceName: z.string(),
      resourceType: ResourceTypeSchema,
      licenseId: z.string().uuid(),
      currentVersion: z.number(),
      availableVersion: z.number(),
      changelog: z.string().nullable(),
      publishedAt: z.string(),
    }),
  ),
});

export const SellerStatsResponseSchema = z.object({
  totalRevenue: z.number(),
  totalSales: z.number(),
  activeListings: z.number(),
  pendingReview: z.number(),
  monthlyStats: z.array(
    z.object({
      month: z.string(),
      sales: z.number(),
      revenue: z.number(),
    }),
  ),
});

export const PurchaseLicenseResponseSchema = z.object({
  sessionId: z.string(),
  url: z.string(),
});

export const ImportVersionResponseSchema = z.object({
  importedResourceId: z.string().uuid(),
  resourceType: ResourceTypeSchema,
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type StoreListing = z.infer<typeof StoreListingSchema>;
export type StoreListingWithDetails = z.infer<typeof StoreListingWithDetailsSchema>;
export type CreateStoreListing = z.infer<typeof CreateStoreListingSchema>;
export type UpdateStoreListing = z.infer<typeof UpdateStoreListingSchema>;

export type StoreLicense = z.infer<typeof StoreLicenseSchema>;
export type StoreLicenseWithDetails = z.infer<typeof StoreLicenseWithDetailsSchema>;
export type PurchaseLicenseRequest = z.infer<typeof PurchaseLicenseRequestSchema>;
export type ImportVersionRequest = z.infer<typeof ImportVersionRequestSchema>;

export type StoreReview = z.infer<typeof StoreReviewSchema>;
export type StoreReviewWithDetails = z.infer<typeof StoreReviewWithDetailsSchema>;
export type CreateStoreReview = z.infer<typeof CreateStoreReviewSchema>;
export type UpdateStoreReview = z.infer<typeof UpdateStoreReviewSchema>;

export type StoreListingVersion = z.infer<typeof StoreListingVersionSchema>;
export type PublishUpdateRequest = z.infer<typeof PublishUpdateRequestSchema>;

export type StoreLicenseHistory = z.infer<typeof StoreLicenseHistorySchema>;

export type GetListingsResponse = z.infer<typeof GetListingsResponseSchema>;
export type GetListingResponse = z.infer<typeof GetListingResponseSchema>;
export type GetReviewsResponse = z.infer<typeof GetReviewsResponseSchema>;
export type GetMyLicensesResponse = z.infer<typeof GetMyLicensesResponseSchema>;
export type GetAvailableUpdatesResponse = z.infer<typeof GetAvailableUpdatesResponseSchema>;
export type SellerStatsResponse = z.infer<typeof SellerStatsResponseSchema>;
export type PurchaseLicenseResponse = z.infer<typeof PurchaseLicenseResponseSchema>;
export type ImportVersionResponse = z.infer<typeof ImportVersionResponseSchema>;

// ============================================================================
// MODERATION SCHEMAS
// ============================================================================

export const GetPendingListingsResponseSchema = z.object({
  listings: z.array(StoreListingWithDetailsSchema),
});

export const GetSellerListingsResponseSchema = z.object({
  listings: z.array(StoreListingWithDetailsSchema),
});

export const ApproveListingRequestSchema = z.object({
  listingId: z.string().uuid(),
});

export const RejectListingRequestSchema = z.object({
  listingId: z.string().uuid(),
  reason: z.string().min(10, "Rejection reason must be at least 10 characters"),
});

export type GetPendingListingsResponse = z.infer<typeof GetPendingListingsResponseSchema>;
export type GetSellerListingsResponse = z.infer<typeof GetSellerListingsResponseSchema>;
export type ApproveListingRequest = z.infer<typeof ApproveListingRequestSchema>;
export type RejectListingRequest = z.infer<typeof RejectListingRequestSchema>;
